import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { User } from '@client/components/_ui/display/User';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { modal } from '@client/components/_ui/feedback/modal';
import { Space } from '@client/components/_ui/layout/Space';
import { UserFilters } from '@client/components/users/UserFilters';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { cn } from '@client/utils';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { Badge, Switch, Tooltip, Typography } from 'antd';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { type Dispatch, type SetStateAction, useId, useState } from 'react';

export const Route = createFileRoute('/console/$orgId/configuration/org-settings/handling-parties/details/$id/users')({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Manage handling party members',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ManageHandlingPartyMembers />
        </PageController>
    );
}

type QueryParams = RouterInputs['user']['listUsers'];

type UserRow = RouterOutputs['user']['listUsers']['rows'][number];

type UserOrderBy = NonNullable<QueryParams['orderBy']>;

const columnHelper = createColumnHelper<UserRow, UserOrderBy>();

type TransferState =
    | { type: 'delta'; added: bigint[]; removed: bigint[] }
    | { type: 'allMembers'; removed: bigint[] }
    | { type: 'allAvailable'; added: bigint[] };

function createBigIntSet(ids: bigint[]): Set<bigint> {
    return new Set(ids);
}

function buildMembersHpFilter(handlingPartyId: bigint, state: TransferState): NonNullable<QueryParams['where']> {
    switch (state.type) {
        case 'delta':
            return {
                OR: [
                    { HandlingParties: { some: { handlingPartyId } }, userId: { notIn: state.removed } },
                    ...(state.added.length > 0 ? [{ userId: { in: state.added } }] : []),
                ],
            };
        case 'allMembers':
            return { userId: { notIn: state.removed } };
        case 'allAvailable':
            return state.added.length > 0 ? { userId: { in: state.added } } : { userId: { in: [] as bigint[] } };
    }
}

function buildAvailableHpFilter(handlingPartyId: bigint, state: TransferState): NonNullable<QueryParams['where']> {
    switch (state.type) {
        case 'delta':
            return {
                OR: [
                    { HandlingParties: { none: { handlingPartyId } }, userId: { notIn: state.added } },
                    ...(state.removed.length > 0 ? [{ userId: { in: state.removed } }] : []),
                ],
            };
        case 'allMembers':
            return state.removed.length > 0 ? { userId: { in: state.removed } } : { userId: { in: [] as bigint[] } };
        case 'allAvailable':
            return { userId: { notIn: state.added } };
    }
}

function computeFinalMemberIds(
    userInfo: { usersInHandlingPartyIds: bigint[]; usersNotInHandlingPartyIds: bigint[] },
    state: TransferState,
): bigint[] {
    switch (state.type) {
        case 'delta': {
            const removedSet = createBigIntSet(state.removed);
            return [...userInfo.usersInHandlingPartyIds.filter((id) => !removedSet.has(id)), ...state.added];
        }
        case 'allMembers': {
            const allIds = [...userInfo.usersInHandlingPartyIds, ...userInfo.usersNotInHandlingPartyIds];
            const removedSet = createBigIntSet(state.removed);
            return allIds.filter((id) => !removedSet.has(id));
        }
        case 'allAvailable':
            return [...state.added];
    }
}

function TransferTable({
    persistKey,
    rows,
    total,
    isLoading,
    queryParams,
    selectedKeys,
    setSelectedKeys,
    setQueryParams,
    refetch,
}: {
    persistKey: string;
    rows?: UserRow[];
    total?: number;
    isLoading: boolean;
    queryParams: QueryParams;
    selectedKeys: bigint[];
    setSelectedKeys: Dispatch<SetStateAction<bigint[]>>;
    setQueryParams: Dispatch<SetStateAction<QueryParams>>;
    refetch: () => Promise<any>;
}) {
    const tableProps = usePrismaTable<UserRow, QueryParams['where'], UserOrderBy>({
        data: rows || [],
        total: total || 0,
        loading: isLoading,
        columns: [
            columnHelper.accessor('fullName', {
                id: 'fullName',
                header: 'Name',
                sortable: 'User.firstName',
                cell: ({ row }) => (
                    <User
                        userId={row.userId}
                        photoUrl={row.photoUrl}
                        name={row.fullName}
                        classNames={{
                            avatar: cn(row.disabled && 'opacity-40'),
                        }}
                    />
                ),
            }),
            columnHelper.accessor('position', {
                id: 'position',
                header: 'Position',
                sortable: 'position',
                cell: ({ value }) => value || '-',
            }),
            columnHelper.accessor((row) => row.User?.email || row.email, {
                id: 'email',
                header: 'Email',
                sortable: 'User.email',
                cell: ({ value, row }) => (
                    <Typography.Link
                        href={`mailto:${value}`}
                        copyable={{ text: value }}
                        className={cn('text-sm', row.disabled && 'text-(--ant-color-text-disabled)')}
                    >
                        {value}
                    </Typography.Link>
                ),
            }),
            columnHelper.accessor('disabled', {
                id: 'status',
                header: 'Status',
                sortable: 'disabled',
                cell: ({ row }) => (
                    <Badge
                        status={row.disabled ? 'error' : 'success'}
                        text={
                            <span
                                className={cn(
                                    'font-medium',
                                    row.disabled ? 'text-status-danger' : 'text-status-success',
                                )}
                            >
                                {row.disabled ? 'Disabled' : 'Active'}
                            </span>
                        }
                    />
                ),
            }),
        ],
        rowKey: 'userId',
        queryParams,
        onParamsChange: setQueryParams,
        pagination: {
            position: 'both',
            showSizeChanger: true,
        },
        columnVisibility: {
            enabled: true,
            persistKey,
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: refetch,
        },
        rowSelection: {
            enabled: true,
            selectedRowKeys: selectedKeys,
            onChange(selectedRowKeys) {
                setSelectedKeys(selectedRowKeys.map(BigInt));
            },
        },
    });

    return (
        <PrismaTable
            {...tableProps}
            onRow={({ disabled, userId }) => ({
                className: cn('cursor-pointer', disabled && 'text-(--ant-color-text-disabled)'),
                onClick() {
                    setSelectedKeys((prev) =>
                        prev.includes(userId) ? prev.filter((key) => key !== userId) : [...prev, userId],
                    );
                },
            })}
        />
    );
}

function ManageHandlingPartyMembers() {
    const overlayContainerId = useId();
    const { id } = Route.useParams();
    const { trpcUtils } = useTrpc();
    const {
        data: userInfo,
        error: userInfoError,
        refetch: userInfoRefetch,
    } = trpc.user.getUserHandlingPartyInfo.useQuery({
        handlingPartyId: id,
    });
    const { mutate, isPending: isSaving } = trpc.handlingParty.updateHandlingParty.useMutation();
    const navigate = useNavigate();

    const [shouldShowDisabledMembers, setShouldShowDisabledMembers] = useState(false);
    const [shouldShowDisabledAvailableUsers, setShouldShowDisabledAvailableUsers] = useState(false);
    const [transferState, setTransferState] = useState<TransferState>({ type: 'delta', added: [], removed: [] });
    const [membersSelectedKeys, setMembersSelectedKeys] = useState<bigint[]>([]);
    const [availableUsersSelectedKeys, setAvailableUsersSelectedKeys] = useState<bigint[]>([]);
    const [membersQueryParams, setMembersQueryParams] = useState<QueryParams>({
        limit: DEFAULT_PAGE_SIZE,
        orderBy: [{ firstName: 'asc' }],
    });
    const [availableUsersQueryParams, setAvailableUsersQueryParams] = useState<QueryParams>({
        limit: DEFAULT_PAGE_SIZE,
        orderBy: [{ firstName: 'asc' }],
    });

    const {
        data: membersData,
        error: membersError,
        isLoading: membersIsLoading,
        refetch: membersRefetch,
    } = trpc.user.listUsers.useQuery({
        ...membersQueryParams,
        where: {
            AND: [
                ...(membersQueryParams.where ? [membersQueryParams.where] : []),
                ...(!shouldShowDisabledMembers ? [{ disabled: false } satisfies QueryParams['where']] : []),
                buildMembersHpFilter(id, transferState),
            ],
        },
    });
    const {
        data: availableUsersData,
        error: availableUsersError,
        isLoading: availableUsersIsLoading,
        refetch: availableUsersRefetch,
    } = trpc.user.listUsers.useQuery({
        ...availableUsersQueryParams,
        where: {
            AND: [
                ...(availableUsersQueryParams.where ? [availableUsersQueryParams.where] : []),
                ...(!shouldShowDisabledAvailableUsers ? [{ disabled: false } satisfies QueryParams['where']] : []),
                buildAvailableHpFilter(id, transferState),
            ],
        },
    });

    return (
        <OverlayPage
            id={overlayContainerId}
            title="Manage handling party members"
            size="xxxLarge"
            afterClose={() => {
                void trpcUtils.handlingParty.invalidate();
                navigate({ moveInMatches: -1 });
            }}
            footer={({ onClose }) => (
                <>
                    <Button type="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        loading={isSaving}
                        disabled={!userInfo}
                        onClick={() => {
                            if (!userInfo) return;
                            message.loading('Saving...');

                            mutate(
                                {
                                    id,
                                    set: {
                                        userIds: computeFinalMemberIds(userInfo, transferState),
                                    },
                                },
                                {
                                    async onSuccess() {
                                        await trpcUtils.handlingParty.invalidate();
                                        await trpcUtils.user.invalidate();

                                        message.success('Saved.');

                                        onClose();
                                    },
                                    onError(error) {
                                        errorMessage.show(error);
                                    },
                                },
                            );
                        }}
                    >
                        Save
                    </Button>
                </>
            )}
        >
            <TRPCErrorBoundary
                errors={[userInfoError, membersError, availableUsersError]}
                onRetry={() => {
                    void userInfoRefetch();
                }}
            >
                <div className="flex gap-2">
                    <div className="w-1 shrink grow basis-1/2">
                        <TableToolbar
                            variant="enhanced"
                            filters={
                                <UserFilters
                                    urlKey="manage-members-filters"
                                    fieldsToHide={{ handlingPartyIds: true }}
                                    onChange={(where) =>
                                        setMembersQueryParams((prev) => ({ ...prev, where, offset: 0 }))
                                    }
                                />
                            }
                        />
                        <div className="mb-2 flex items-center justify-between">
                            <Typography.Text className="font-medium text-base">Members</Typography.Text>
                            <Space>
                                <Tooltip
                                    title={
                                        shouldShowDisabledMembers
                                            ? 'Showing all members'
                                            : 'Showing only active members'
                                    }
                                >
                                    <Switch
                                        checked={shouldShowDisabledMembers}
                                        onChange={setShouldShowDisabledMembers}
                                        checkedChildren="All"
                                        unCheckedChildren="Active"
                                    />
                                </Tooltip>
                                <Button
                                    type="primary"
                                    disabled={!membersData?.rows?.length}
                                    onClick={() =>
                                        modal.confirm({
                                            width: 500,
                                            title: 'Remove all members from the handling party?',
                                            content: (
                                                <>
                                                    You're about to remove all members of this handling party.
                                                    <br />
                                                    Do you want to continue?
                                                </>
                                            ),
                                            okText: 'Continue',
                                            onOk() {
                                                setTransferState({ type: 'allAvailable', added: [] });
                                                setMembersSelectedKeys([]);
                                                setAvailableUsersSelectedKeys([]);
                                            },
                                        })
                                    }
                                >
                                    Remove all members
                                </Button>
                            </Space>
                        </div>
                        <TransferTable
                            persistKey="manage-members-table-columns"
                            rows={membersData?.rows || []}
                            total={membersData?.total}
                            isLoading={membersIsLoading}
                            queryParams={membersQueryParams}
                            selectedKeys={membersSelectedKeys}
                            setSelectedKeys={setMembersSelectedKeys}
                            setQueryParams={setMembersQueryParams}
                            refetch={membersRefetch}
                        />
                    </div>
                    <div className="mt-[30vh] flex min-w-48 flex-col gap-1">
                        <Button
                            type="primary"
                            size="small"
                            disabled={!availableUsersSelectedKeys.length}
                            icon={<ArrowLeft size={12} className="mb-px" />}
                            onClick={() => {
                                const selectedIds = availableUsersSelectedKeys;
                                setTransferState((prev) => {
                                    if (prev.type === 'allAvailable')
                                        return { ...prev, added: [...prev.added, ...selectedIds] };
                                    const selectedIdsSet = createBigIntSet(selectedIds);

                                    if (prev.type === 'allMembers')
                                        return {
                                            ...prev,
                                            removed: prev.removed.filter((id) => !selectedIdsSet.has(id)),
                                        };
                                    const removedSet = createBigIntSet(prev.removed);
                                    const newRemoved = prev.removed.filter((id) => !selectedIdsSet.has(id));
                                    const idsNotInRemoved = selectedIds.filter((id) => !removedSet.has(id));
                                    return {
                                        type: 'delta',
                                        added: [...prev.added, ...idsNotInRemoved],
                                        removed: newRemoved,
                                    };
                                });
                                setAvailableUsersSelectedKeys([]);
                            }}
                        >
                            Add {availableUsersSelectedKeys.length > 1 ? `${availableUsersSelectedKeys.length} ` : null}
                            {availableUsersSelectedKeys.length > 1 ? 'members' : 'member'}
                        </Button>
                        <Button
                            type="primary"
                            size="small"
                            disabled={!membersSelectedKeys.length}
                            icon={<ArrowRight size={12} className="mb-px" />}
                            onClick={() => {
                                const selectedIds = membersSelectedKeys;
                                setTransferState((prev) => {
                                    if (prev.type === 'allMembers')
                                        return { ...prev, removed: [...prev.removed, ...selectedIds] };
                                    const selectedIdsSet = createBigIntSet(selectedIds);

                                    if (prev.type === 'allAvailable')
                                        return {
                                            ...prev,
                                            added: prev.added.filter((id) => !selectedIdsSet.has(id)),
                                        };
                                    const addedSet = createBigIntSet(prev.added);
                                    const newAdded = prev.added.filter((id) => !selectedIdsSet.has(id));
                                    const idsNotInAdded = selectedIds.filter((id) => !addedSet.has(id));
                                    return {
                                        type: 'delta',
                                        added: newAdded,
                                        removed: [...prev.removed, ...idsNotInAdded],
                                    };
                                });
                                setMembersSelectedKeys([]);
                            }}
                        >
                            Remove {membersSelectedKeys.length > 1 ? `${membersSelectedKeys.length} ` : null}
                            {membersSelectedKeys.length > 1 ? 'members' : 'member'}
                        </Button>
                    </div>
                    <div className="w-1 shrink grow basis-1/2">
                        <TableToolbar
                            variant="enhanced"
                            filters={
                                <UserFilters
                                    urlKey="manage-available-users-filters"
                                    fieldsToHide={{ handlingPartyIds: true }}
                                    onChange={(where) =>
                                        setAvailableUsersQueryParams((prev) => ({ ...prev, where, offset: 0 }))
                                    }
                                />
                            }
                        />
                        <div className="mb-2 flex items-center justify-between">
                            <Typography.Text className="font-medium text-base">Available Users</Typography.Text>
                            <Space>
                                <Tooltip
                                    title={
                                        shouldShowDisabledAvailableUsers
                                            ? 'Showing all available users'
                                            : 'Showing only active available users'
                                    }
                                >
                                    <Switch
                                        checked={shouldShowDisabledAvailableUsers}
                                        onChange={setShouldShowDisabledAvailableUsers}
                                        checkedChildren="All"
                                        unCheckedChildren="Active"
                                    />
                                </Tooltip>
                                <Button
                                    type="primary"
                                    onClick={() =>
                                        modal.confirm({
                                            width: 540,
                                            title: 'Add all users to the handling party?',
                                            content: (
                                                <>
                                                    You're about to add all users in the organisation to this handling
                                                    party.
                                                    <br />
                                                    Do you want to continue?
                                                </>
                                            ),
                                            okText: 'Continue',
                                            onOk() {
                                                setTransferState({ type: 'allMembers', removed: [] });
                                                setMembersSelectedKeys([]);
                                                setAvailableUsersSelectedKeys([]);
                                            },
                                        })
                                    }
                                >
                                    Add all users
                                </Button>
                            </Space>
                        </div>
                        <TransferTable
                            persistKey="manage-available-users-table-columns"
                            rows={availableUsersData?.rows || []}
                            total={availableUsersData?.total}
                            isLoading={availableUsersIsLoading}
                            queryParams={availableUsersQueryParams}
                            selectedKeys={availableUsersSelectedKeys}
                            setSelectedKeys={setAvailableUsersSelectedKeys}
                            setQueryParams={setAvailableUsersQueryParams}
                            refetch={availableUsersRefetch}
                        />
                    </div>
                </div>
            </TRPCErrorBoundary>
        </OverlayPage>
    );
}
